import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Clock, Facebook, Star } from "lucide-react";
import { LOGO_URL, BRAND } from "@/lib/brand";

export default function Footer() {
  return (
    <footer className="border-t border-soft bg-primary text-primary-foreground" data-testid="site-footer">
      <div className="mx-auto max-w-7xl px-6 md:px-10 py-16 grid gap-12 md:grid-cols-4">
        <div className="md:col-span-2">
          <Link to="/" className="inline-flex items-center gap-4" data-testid="footer-brand">
            <img
              src={LOGO_URL}
              alt="Pit Stop Motors — Idaho"
              className="h-20 md:h-24 w-auto select-none"
              draggable={false}
            />
            <div className="flex flex-col leading-none">
              <span className="overline text-[10px] !text-accent-bright">Idaho · {BRAND.dealerNumber}</span>
              <span className="font-serif text-xl mt-2">Drive with Faith</span>
            </div>
          </Link>
          <p className="mt-6 max-w-md text-sm leading-relaxed text-primary-foreground/70 font-sans">
            A family-owned, faith-rooted dealership serving Idaho with honesty, fair pricing, and real care. Every customer is treated like a neighbor &mdash; because they are.
          </p>
          <p className="mt-4 italic font-serif text-base text-accent-bright" data-testid="footer-verse">
            &ldquo;Whatever you do, work at it with all your heart, as working for the Lord.&rdquo; <span className="not-italic text-xs text-primary-foreground/60">— Colossians 3:23</span>
          </p>
          <div className="mt-6 flex items-center gap-3" data-testid="footer-socials">
            <a
              href={BRAND.facebookUrl}
              target="_blank"
              rel="noreferrer"
              aria-label="Pit Stop Motors on Facebook"
              className="inline-flex h-9 w-9 items-center justify-center border border-primary-foreground/20 hover:border-accent hover:text-accent-bright transition-colors"
              data-testid="footer-facebook"
            >
              <Facebook className="h-4 w-4" />
            </a>
            <a
              href={BRAND.carsforsaleUrl}
              target="_blank"
              rel="noreferrer"
              aria-label="Pit Stop Motors on Carsforsale.com"
              className="inline-flex h-9 items-center px-3 border border-primary-foreground/20 hover:border-accent hover:text-accent-bright transition-colors text-xs uppercase tracking-wider"
              data-testid="footer-carsforsale"
            >
              Carsforsale.com
            </a>
            <a
              href={BRAND.googleReviewUrl}
              target="_blank"
              rel="noreferrer"
              aria-label="Review Pit Stop Motors on Google"
              className="inline-flex h-9 items-center gap-1.5 px-3 border border-primary-foreground/20 hover:border-accent hover:text-accent-bright transition-colors text-xs uppercase tracking-wider"
              data-testid="footer-google-review"
            >
              <Star className="h-3.5 w-3.5 text-gold fill-gold" /> Review us on Google
            </a>
          </div>
        </div>

        <div>
          <h4 className="font-serif text-lg mb-4">Visit</h4>
          <ul className="space-y-3 text-sm text-primary-foreground/70">
            <li className="flex items-start gap-2">
              <MapPin className="h-4 w-4 mt-0.5 text-accent-bright shrink-0" />
              <a
                href={BRAND.mapsHref}
                target="_blank"
                rel="noreferrer"
                className="hover:text-accent-bright transition-colors"
                data-testid="footer-address"
              >
                {BRAND.addressLine1}<br/>{BRAND.addressLine2}
              </a>
            </li>
            <li className="flex items-center gap-2">
              <Phone className="h-4 w-4 text-accent-bright" />
              <a href={BRAND.phoneHref} className="hover:text-accent-bright transition-colors" data-testid="footer-phone">
                {BRAND.phone}
              </a>
            </li>
            <li className="flex items-start gap-2">
              <Mail className="h-4 w-4 mt-0.5 text-accent-bright shrink-0" />
              <a href={`mailto:${BRAND.email}`} className="hover:text-accent-bright transition-colors break-all" data-testid="footer-email">
                {BRAND.email}
              </a>
            </li>
            <li className="flex items-start gap-2"><Clock className="h-4 w-4 mt-0.5 text-accent-bright" />Mon–Sat: 9am–7pm<br/>Sunday: Closed</li>
          </ul>
        </div>

        <div>
          <h4 className="font-serif text-lg mb-4">Explore</h4>
          <ul className="space-y-2 text-sm text-primary-foreground/70">
            <li><Link to="/inventory" className="hover:text-accent-bright transition-colors" data-testid="footer-link-inventory">Inventory</Link></li>
            <li><Link to="/financing" className="hover:text-accent-bright transition-colors" data-testid="footer-link-financing">Financing</Link></li>
            <li><Link to="/about" className="hover:text-accent-bright transition-colors" data-testid="footer-link-about">About Us</Link></li>
            <li><Link to="/contact" className="hover:text-accent-bright transition-colors" data-testid="footer-link-contact">Contact Us</Link></li>
            <li>
              <a
                href={`${BRAND.publicApi}/public/listings`}
                className="hover:text-accent-bright transition-colors"
                data-testid="footer-link-public-api"
                target="_blank"
                rel="noreferrer"
              >
                Public Listings API
              </a>
            </li>
            <li>
              <a
                href={`${BRAND.publicApi}/feed.rss`}
                className="hover:text-accent-bright transition-colors"
                data-testid="footer-link-rss"
                target="_blank"
                rel="noreferrer"
              >
                Inventory RSS Feed
              </a>
            </li>
          </ul>
        </div>
      </div>
      <div className="border-t border-primary-foreground/10 py-6 text-center text-xs text-primary-foreground/50">
        © {new Date().getFullYear()} Pit Stop Motors · {BRAND.dealerNumber} · {BRAND.domain} · All glory to God
      </div>
    </footer>
  );
}
